import { SubscriptionBillingCycleEnum, SubscriptionPlanCategoryEnum } from '../enums/PartnerSubscription';
import { FrontendPaymentSource } from '../enums/FrontendPaymentSource';
import { PaymentMethodEnum } from '../enums/PaymentMethodEnum';
import { getPlanPrice } from '../helpers/utils';
import { ApplicantListItem } from '../interfaces/applicants/ApplicantListItem';
import { Partner, PartnerUser } from '../interfaces/partner/PartnerProfile';
import { SubscriptionPlanInterface } from '../interfaces/payment/SubscrptionPlanInterface';
import { SubscriptionPurchase } from '../interfaces/product/ProductPurchase';
import { FullTimeJobDetails } from '../interfaces/job/JobDetails';
import { CvSearchFiltersInterface } from '../interfaces/cv-search/CvSearchInterfaces';
import { getUtmDataFromCookies } from '../utm/cookies';
import { UtmObject } from '../utm/types';

const recentMarketingEvents = new Map<string, number>();

export const getMarketingEventDedupeKey = (eventName: string, partnerId?: string) =>
    `${eventName}:${partnerId ?? 'anonymous'}:${window.location.pathname}`;

export const shouldTrackMarketingEventOnce = (key: string, windowMs = 10000) => {
    const now = Date.now();
    const lastTrackedAt = recentMarketingEvents.get(key);
    if (lastTrackedAt && now - lastTrackedAt < windowMs) return false;
    recentMarketingEvents.set(key, now);
    return true;
};

export const clearMarketingEventDedupe = (key: string) => {
    recentMarketingEvents.delete(key);
};

export const getCompanyId = (partner?: Partner) => partner?.partnerId || partner?.id;

export const getPartnerUserId = (partnerUser?: PartnerUser) => partnerUser?.id?.split('-')?.[0];

export const hashPiiValue = async (value?: string): Promise<string | undefined> => {
    if (!value?.trim()) return undefined;
    const normalized = value.trim().toLowerCase();
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    return Array.from(new Uint8Array(hashBuffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const normalizeMobileToE164 = (mobile?: string): string | undefined => {
    if (!mobile?.trim()) return undefined;
    const cleaned = mobile.trim().replace(/\s/g, '');
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

export const getPlanNameFromCategory = (category?: SubscriptionPlanCategoryEnum | string | null) => {
    switch (category) {
        case SubscriptionPlanCategoryEnum.CORE:
            return 'basic';
        case SubscriptionPlanCategoryEnum.RISE:
        case SubscriptionPlanCategoryEnum.GROWTH:
            return 'premium';
        case SubscriptionPlanCategoryEnum.ENTERPRISE:
            return 'enterprise';
        case SubscriptionPlanCategoryEnum.FREE:
            return 'free';
        case SubscriptionPlanCategoryEnum.WATAN:
            return 'watan';
        default:
            return 'free';
    }
};

export const getSubscriptionDurationLabel = (billingCycle?: SubscriptionBillingCycleEnum) => {
    switch (billingCycle) {
        case SubscriptionBillingCycleEnum.MONTHLY:
            return '1m';
        case SubscriptionBillingCycleEnum.BIMONTHLY:
            return '2m';
        case SubscriptionBillingCycleEnum.QUARTERLY:
            return '3m';
        case SubscriptionBillingCycleEnum.YEARLY:
            return '12m';
        default:
            return undefined;
    }
};

export const getSubscriptionDurationMonths = (billingCycle?: SubscriptionBillingCycleEnum) => {
    switch (billingCycle) {
        case SubscriptionBillingCycleEnum.MONTHLY:
            return 1;
        case SubscriptionBillingCycleEnum.BIMONTHLY:
            return 2;
        case SubscriptionBillingCycleEnum.QUARTERLY:
            return 3;
        case SubscriptionBillingCycleEnum.YEARLY:
            return 12;
        default:
            return undefined;
    }
};

export const buildPlanAddOns = (plan: SubscriptionPlanInterface) => {
    const addOns: string[] = [];

    if (plan.unlocksPerResetCycle) {
        addOns.push(`${plan.unlocksPerResetCycle} unlocks`);
    }

    if (plan.features?.hasVideoCv && plan.category !== SubscriptionPlanCategoryEnum.CORE) {
        addOns.push('unlimited video interviews');
    }

    if (plan.category === SubscriptionPlanCategoryEnum.ENTERPRISE) {
        addOns.push('saud ai interviews');
    }

    return addOns.length ? addOns.join(' | ') : undefined;
};

export const getCurrentPlanName = (partner?: Partner) =>
    getPlanNameFromCategory(partner?.currentSubscription?.subscriptionCategory);

export const buildSubscriptionMetadata = (
    plan: SubscriptionPlanInterface,
    billingCycle: SubscriptionBillingCycleEnum,
    jobsCount: number,
    lang: 'en' | 'ar' = 'en',
) => {
    const pricing = getPlanPrice(plan, billingCycle, jobsCount);
    return {
        currency: 'SAR',
        value: pricing?.total ?? pricing?.subtotal ?? plan.monthlyBillingPrice,
        item_id: plan.externalId,
        item_name: plan.name?.[lang] || plan.name?.en,
        plan_name: getPlanNameFromCategory(plan.category),
        subscription_duration: getSubscriptionDurationLabel(billingCycle),
        job_count: jobsCount,
    };
};

export const buildPlanSelectedMetadata = (
    plan: SubscriptionPlanInterface,
    billingCycle: SubscriptionBillingCycleEnum,
    jobsCount: number,
    lang: 'en' | 'ar' = 'en',
) => {
    const pricing = getPlanPrice(plan, billingCycle, jobsCount);
    const planPrice = pricing?.subtotal ?? pricing?.total ?? plan.monthlyBillingPrice;
    const amount = pricing?.total ?? planPrice;
    const addOns = buildPlanAddOns(plan);

    return {
        currency: 'SAR',
        plan_name: getPlanNameFromCategory(plan.category),
        plan_price: planPrice,
        amount,
        jobs_posted: jobsCount,
        duration: getSubscriptionDurationMonths(billingCycle),
        ...(addOns ? { add_ons: addOns } : {}),
        value: amount,
        item_id: plan.externalId,
        item_name: plan.name?.[lang] || plan.name?.en,
        subscription_duration: getSubscriptionDurationLabel(billingCycle),
        job_count: jobsCount,
    };
};

export const buildSubscriptionMetadataFromPurchase = (
    preparePurchaseData: SubscriptionPurchase,
    plan?: SubscriptionPlanInterface,
    lang: 'en' | 'ar' = 'en',
) => {
    if (!plan) {
        return {
            currency: 'SAR',
            value: preparePurchaseData.amount ?? preparePurchaseData.total,
            item_id: preparePurchaseData.planId,
            plan_name: getPlanNameFromCategory(preparePurchaseData.planCategory),
            subscription_duration: getSubscriptionDurationLabel(preparePurchaseData.billingCycle),
            job_count: preparePurchaseData.jobsCount,
        };
    }

    return buildSubscriptionMetadata(
        plan,
        preparePurchaseData.billingCycle,
        preparePurchaseData.jobsCount ?? plan.jobsPerResetCycle,
        lang,
    );
};

export const buildApplicantProfileMetadata = (
    jobDetails: FullTimeJobDetails,
    applicant: ApplicantListItem,
    applicantRank?: number,
) => ({
    candidate_id: applicant.externalId,
    job_id: jobDetails?.externalId,
    ...(applicantRank != null ? { applicant_rank: applicantRank } : {}),
    ...(applicant.nationality ? { candidate_nationality: applicant.nationality } : {}),
    ...(applicant.gender ? { candidate_gender: applicant.gender } : {}),
    is_recommended: !applicant.appliedAt,
    is_unlocked: applicant.isUnlocked,
});

/** Flat primitives only. pwa_ keeps these off GA4's reserved campaign parameter names. */
const buildLastUtmMetadata = (lastUtm?: UtmObject | null): Record<string, string | number> => {
    if (!lastUtm) return {};

    return Object.entries(lastUtm).reduce<Record<string, string | number>>((fields, [key, value]) => {
        if (value == null || value === '') return fields;
        fields[`pwa_${key}`] = value;
        return fields;
    }, {});
};

export const getLastUtmEventMetadata = (): Record<string, string | number> =>
    buildLastUtmMetadata(getUtmDataFromCookies()?.lastUtm);

export const buildRegistrationStartedMetadata = (entryPoint: string, language: string) => ({
    entry_point: entryPoint,
    language_selected: language,
    ...getLastUtmEventMetadata(),
});

export const buildRegistrationAbandonedMetadata = (stepReached: string, timeOnPageSec: number) => ({
    step_reached: stepReached,
    time_on_page: timeOnPageSec,
});

export const buildCvDatabaseSearchedMetadata = ({
    searchQuery,
    filtersApplied,
    resultsCount,
}: {
    searchQuery: string;
    filtersApplied: string;
    resultsCount: number;
}) => ({
    search_query: searchQuery,
    filters_applied: filtersApplied,
    results_count: resultsCount,
});

export const buildCvSearchFiltersLabel = (filters?: CvSearchFiltersInterface) => {
    if (!filters) return '';

    const activeFilters = Object.entries(filters)
        .filter(([, value]) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0))
        .map(([key]) => key);

    return activeFilters.join(', ');
};

export const buildAiInterviewRequestedMetadata = (candidateId: string, interviewType: string) => ({
    candidate_id: candidateId,
    interview_type: interviewType,
});

export const buildRegistrationCompletedMetadata = async ({
    email,
    mobile,
    companyName,
    city,
    companySize,
    industry,
    leadSource,
    registrationMethod = 'phone',
}: {
    email?: string;
    mobile?: string;
    companyName?: string;
    city?: string;
    companySize?: string;
    industry?: string;
    leadSource?: string;
    registrationMethod?: string;
}) => {
    const e164Mobile = normalizeMobileToE164(mobile);
    const hashedEmail = await hashPiiValue(email);
    const hashedMobile = e164Mobile ? await hashPiiValue(e164Mobile) : undefined;

    return {
        email: hashedEmail,
        mobile_number: hashedMobile,
        mobile: hashedMobile,
        company_name: companyName,
        city,
        company_size: companySize,
        businessSize: companySize,
        ...(industry ? { industry } : {}),
        ...(leadSource ? { leadSource } : {}),
        registration_method: registrationMethod,
    };
};

export const buildJobPublishedMetadata = ({
    partner,
    jobId,
    jobTitle,
    contractType,
    workplaceType,
    fullLocationName,
    planName,
    isFreeJob,
}: {
    partner?: Partner;
    jobId: string;
    jobTitle: string;
    contractType?: string;
    workplaceType?: string;
    fullLocationName?: string;
    planName?: string;
    isFreeJob?: boolean;
}) => ({
    company_id: getCompanyId(partner),
    job_id: jobId,
    job_title: jobTitle,
    ...(contractType ? { contract_type: contractType } : {}),
    ...(workplaceType ? { workplace_type: workplaceType } : {}),
    ...(fullLocationName ? { full_location_name: fullLocationName } : {}),
    ...(planName ? { plan_name: planName } : {}),
    ...(isFreeJob != null ? { is_free_job: isFreeJob } : {}),
});

export const mapFrontendPaymentSourceToPaywallType = (source?: FrontendPaymentSource | string): string => {
    switch (source) {
        case FrontendPaymentSource.VIDEO_CV:
            return 'video';
        case FrontendPaymentSource.PDF_CV_APPLICANT:
        case FrontendPaymentSource.PDF_CV_CV_SEARCH:
            return 'cv_download';
        case FrontendPaymentSource.APPLICANT_DETAILS:
        case FrontendPaymentSource.APPLICANT_DETAILS_SUBSCRIBED:
        case FrontendPaymentSource.CHAT:
            return 'contact';
        case FrontendPaymentSource.ADVANCED_FILTERS:
            return 'applicant_view';
        default:
            return 'applicant_view';
    }
};

export const mapPaymentMethodToAnalytics = (method: PaymentMethodEnum) => {
    switch (method) {
        case PaymentMethodEnum.APPLE_PAY:
            return 'applepay';
        case PaymentMethodEnum.BANK_TRANSFER:
            return 'bank_transfer';
        case PaymentMethodEnum.CARD:
        default:
            return 'visa';
    }
};

export const buildPaywallHitMetadata = (
    partner?: Partner,
    paywallType?: string,
    sourcePage?: string,
    balances?: {
        maxUnlocksPerResetCycle?: number;
        unlocksRemainingPerResetCycle?: number;
        maxJobsPerResetCycle?: number;
        jobsRemainingPerResetCycle?: number;
        remainingVideoInterviewViewsPerResetCycle?: number;
    },
) => {
    const metadata: Record<string, unknown> = {
        company_id: getCompanyId(partner),
        paywall_type: paywallType || 'applicant_view',
        current_plan: getCurrentPlanName(partner),
        ...(sourcePage ? { source_page: sourcePage } : {}),
    };

    if (paywallType === 'contact' && balances?.maxUnlocksPerResetCycle != null) {
        metadata.limit_value = balances.maxUnlocksPerResetCycle;
        metadata.value_consumed_to_date = (balances.maxUnlocksPerResetCycle ?? 0) - (balances.unlocksRemainingPerResetCycle ?? 0);
    } else if (paywallType === 'video' && balances?.remainingVideoInterviewViewsPerResetCycle != null) {
        metadata.limit_value = balances.remainingVideoInterviewViewsPerResetCycle;
    } else if (paywallType === 'applicant_view' && balances?.maxJobsPerResetCycle != null) {
        metadata.limit_value = balances.maxJobsPerResetCycle;
        metadata.value_consumed_to_date = (balances.maxJobsPerResetCycle ?? 0) - (balances.jobsRemainingPerResetCycle ?? 0);
    }

    return metadata;
};

export const buildApplicantActionMetadata = ({
    applicant,
    jobId,
    companyId,
    extra = {},
}: {
    applicant?: ApplicantListItem;
    jobId?: string;
    companyId?: string;
    extra?: Record<string, unknown>;
}) => ({
    ...(companyId ? { company_id: companyId } : {}),
    ...(applicant?.externalId ? { candidate_id: applicant.externalId } : {}),
    ...(jobId ? { job_id: jobId } : {}),
    ...extra,
});

export const getJobTitleLabelFromForm = (jobTitle: any): string | undefined => {
    if (!jobTitle) return undefined;
    if (typeof jobTitle === 'string') return jobTitle.trim() || undefined;
    return jobTitle.label || jobTitle.value || jobTitle.jobTitle?.en || jobTitle.jobTitle?.ar || undefined;
};

export const getLocationLabelFromForm = (jobLocation: any, lang: 'en' | 'ar' = 'en'): string | undefined => {
    if (!jobLocation) return undefined;
    if (Array.isArray(jobLocation)) {
        return jobLocation.map((loc) => loc?.label || loc?.localizedAddress?.[lang] || loc?.displayName).filter(Boolean).join(' | ');
    }
    return jobLocation.label || jobLocation.localizedAddress?.[lang] || jobLocation.displayName;
};

export const mapFilterKeysToAnalytics = (selectedFilters: Record<string, unknown>) =>
    Object.keys(selectedFilters).filter((key) => {
        const value = selectedFilters[key];
        return value != null && value !== '' && !(Array.isArray(value) && value.length === 0);
    }).join(' | ');

